import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ReportDocumentRenderer } from "../features/reports/components/ReportDocumentRenderer";
import { ReportProgress } from "../features/reports/components/ReportProgress";
import { useReportPolling } from "../features/reports/useReportPolling";
import { AppError } from "../lib/errors";
import { REPORT_RUN_DELETE_BLOCKED_STATUSES, ReportPlanState } from "../lib/report";
import { reportClient } from "../lib/reportClient";

// backend App\Constants\ReportConstant::STATUS_*を実際に確認した上での
// 全実在statusに対する表示分岐(真っ白にしないための網羅的if/elseで、最後にfallbackを持つ)。
function ReportRunBody({ run }: { run: ReportPlanState }) {
  if (run.status === "planning") {
    return (
      <section className="rounded-2xl border border-[#EADBC8] bg-white p-5">
        <p className="text-sm font-bold text-[#6B5851]">取材計画を作成しています。しばらくお待ちください。</p>
      </section>
    );
  }

  if (run.status === "plan_ready") {
    return (
      <section className="rounded-2xl border border-[#EADBC8] bg-white p-5">
        <p className="text-sm font-bold text-[#6B5851]">取材計画の内容確認・開始をお待ちしています。</p>
        <Link
          to={`/request/deep-report?runId=${encodeURIComponent(run.runId)}`}
          className="mt-4 inline-flex min-h-[42px] items-center justify-center rounded-full bg-[#7C5A4E] px-5 py-2 text-sm font-black text-white shadow-sm transition hover:bg-[#5D4D46]"
        >
          計画を確認して開始する
        </Link>
      </section>
    );
  }

  if (run.status === "queued" || run.status === "running") {
    return <ReportProgress stage={run.stage ?? "queued"} progress={run.progress} />;
  }

  if (run.status === "cancelled") {
    return (
      <section className="rounded-2xl bg-[#F2E7DC] p-5">
        <h1 className="font-black text-[#7C5A4E]">レポートの作成はキャンセルされました</h1>
      </section>
    );
  }

  if (run.status === "failed") {
    return (
      <section className="rounded-2xl bg-[#FFF0EB] p-5">
        <h1 className="font-black text-[#B4533A]">レポートを作成できませんでした</h1>
        <p className="mt-2 text-sm">{run.error?.message || "時間をおいて、もう一度お試しください。"}</p>
      </section>
    );
  }

  if (run.status === "partial") {
    return (
      <div className="grid gap-4">
        <section className="rounded-2xl bg-[#FFF0EB] p-5">
          <h1 className="font-black text-[#B4533A]">一部の観点を完了できませんでした</h1>
          <p className="mt-2 text-sm text-[#8A746A]">
            読める範囲の内容を下に表示しています。P2では同じレポートの再実行は提供していないため、改めて調べ直したい場合は
            <Link to="/request/deep-report" className="font-bold text-[#A67C52] underline">
              新しくレポートを依頼
            </Link>
            してください。
          </p>
        </section>
        <ReportDocumentSection run={run} />
      </div>
    );
  }

  // succeeded、または未知のstatus文字列が来た場合もここに落ちる(真っ白防止)。
  return <ReportDocumentSection run={run} />;
}

/**
 * currentDocumentの本文・注意事項表示。succeeded/partialで共通する。
 * warningsはAgentの内部診断文字列(例:「angle a2: no results」)をそのまま
 * 保持しているため(App\Services\Report\ReportCallbackService::handle()が
 * 無加工でwarnings_jsonへ保存)、利用者向け文言への変換はP2未対応。
 */
function ReportDocumentSection({ run }: { run: ReportPlanState }) {
  if (!run.currentDocument) {
    return (
      <section className="rounded-2xl border border-[#EADBC8] bg-white p-5">
        <p className="text-sm font-bold text-[#6B5851]">本文を取得できませんでした。時間をおいて再度お試しください。</p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-[#EADBC8] bg-white p-5 shadow-sm sm:p-8">
      {run.currentDocument.document ? (
        <ReportDocumentRenderer document={run.currentDocument.document} />
      ) : (
        <div>
          <p className="text-sm font-bold text-[#B4533A]">レポートの表示形式が更新されているため、簡易表示のみとなります。</p>
          <pre className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-[#4A3E39]">{run.currentDocument.plainText}</pre>
        </div>
      )}
      {run.warnings && run.warnings.length > 0 && (
        <div className="mt-8 rounded-xl bg-[#FFF5E8] p-4">
          <h2 className="font-bold">注意事項</h2>
          {run.warnings.map((item, index) => (
            <p key={index} className="mt-1 text-sm">
              {item}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ReportDetailPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { run, loading, error, transientMessage } = useReportPolling(runId);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // research-report-redesign-screen-spec.md「共通ヘッダー」: APIの削除可能集合
  // (queued/running以外)に合わせて削除menuを表示する。
  const canDelete = !!run && !REPORT_RUN_DELETE_BLOCKED_STATUSES.includes(run.status);

  const handleDelete = async () => {
    if (!runId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await reportClient.deleteRun(runId);
      navigate("/reports", { replace: true });
    } catch (reason) {
      setDeleteError(reason instanceof AppError ? reason.message : "削除に失敗しました。時間をおいて再度お試しください。");
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FFF9F5] px-4 py-8 text-[#4A3E39]">
      <div className="mx-auto max-w-4xl">
        <div className="print:hidden flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-xl font-black text-[#7C5A4E]">深掘りレポート</h1>
          <div className="flex flex-wrap items-start gap-2">
            <button
              onClick={() => window.print()}
              disabled={!run?.currentDocument}
              className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-bold disabled:opacity-40"
            >
              印刷
            </button>
            {canDelete && (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="rounded-full border border-[#D8887B] px-4 py-2 text-sm font-bold text-[#B4533A]"
              >
                削除
              </button>
            )}
          </div>
        </div>
        {loading && !run && <p className="mt-8 text-center">読み込み中...</p>}
        {transientMessage && (
          <p role="status" className="mt-5 rounded-xl bg-[#FFF8E8] p-3 text-[#7A6547]">
            {transientMessage}
          </p>
        )}
        {error && (
          <p role="alert" className="mt-5 rounded-xl bg-[#FFF0EB] p-3 text-[#B4533A]">
            {error}
          </p>
        )}
        {deleteError && (
          <p role="alert" className="mt-5 rounded-xl bg-[#FFF0EB] p-3 text-[#B4533A]">
            {deleteError}
          </p>
        )}
        {run && (
          <div className="mt-6">
            <ReportRunBody run={run} />
          </div>
        )}
      </div>

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" aria-label="レポートを削除しますか？" className="w-full max-w-md rounded-[2rem] bg-white p-5 shadow-lg">
            <h2 className="text-lg font-black text-[#5D4D46]">レポートを削除しますか？</h2>
            <p className="mt-3 text-sm leading-6 text-[#8A746A]">
              「{run?.theme || "このレポート"}」を削除すると元に戻せません。
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                className="rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52]"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-full bg-[#A1493E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#8C3A33] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleting ? "削除中..." : "削除する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
